import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Chip,
  Box,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import ClientSubscriptionTransactions from "../ClientPortal/ClientSubscriptionTransactions";

// User-products API returns array or { items: [...] }; normalize to array with priceId set
const toUserProductsList = (response) => {
  const list = Array.isArray(response)
    ? response
    : response?.items && Array.isArray(response.items)
      ? response.items
      : [];
  return list.map((p) => ({
    ...p,
    priceId: p.priceId ?? p.price_id ?? "",
  }));
};

// Build a table row from a user-product item (purchased or assigned; API has productName, priceDescription, price, etc.)
const userProductToRow = (item, isPurchased) => ({
  product: {
    name: item.productName ?? "Product",
    productId: item.productId ?? item.product_id,
  },
  price: {
    priceId: item.priceId,
    unitAmount: item.unitAmount ?? item.unit_amount ?? item.price ?? 0,
    metadata: {
      description: item.priceDescription ?? item.description ?? "",
    },
    recurring: item.recurring,
  },
  purchasedProduct: isPurchased ? item : undefined,
  assignedProduct: !isPurchased ? item : undefined,
  isPurchased,
  isAssigned: !isPurchased,
});

const ManageProductsDialog = ({
  open,
  onClose,
  onSaved,
  client,
  initialProducts,
  initialPurchasedProducts,
}) => {
  const {
    getUserProducts,
    postUserProducts,
    patchUserProduct,
    cancelSubscription,
    uncancelSubscription,
  } = useApi();
  const { stripeAccountId } = useUserInfo();
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [clientPurchasedProducts, setClientPurchasedProducts] = useState([]);
  const [quantityValues, setQuantityValues] = useState({});
  const [subscriptionActionLoading, setSubscriptionActionLoading] =
    useState(null);
  const [activeTab, setActiveTab] = useState("purchased");
  const [confirmState, setConfirmState] = useState(null);

  const loadProductsData = async () => {
    if (!client) return;
    setProductsLoading(true);
    try {
      const [purchasedRes, assignedRes] = await Promise.all([
        getUserProducts(client.id, "purchased"),
        getUserProducts(client.id, "assigned"),
      ]);
      const clientPurchasedProducts = toUserProductsList(purchasedRes);
      const clientProducts = toUserProductsList(assignedRes);

      setAssignedProducts(clientProducts);
      setClientPurchasedProducts(clientPurchasedProducts);
      const purchasedPriceIds = new Set(
        clientPurchasedProducts.map((p) => p.priceId).filter(Boolean)
      );
      const assignedPriceIds = clientProducts
        .filter((p) => !purchasedPriceIds.has(p.priceId))
        .map((p) => p.priceId);
      setSelectedProducts(assignedPriceIds);
    } catch (error) {
      console.error("Failed to load products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (open && client) {
      setActiveTab("purchased");
      setQuantityValues({});
      loadProductsData();
    }
  }, [open, client?.id]);

  const handleSave = async () => {
    try {
      if (Object.keys(quantityValues).length > 0) {
        await Promise.all(
          Object.entries(quantityValues).map(([priceId, value]) =>
            handleQuantityChange(priceId, value)
          )
        );
      }
      if (activeTab === "assigned") {
        const items = selectedProducts
          .map((priceId) => {
            const a = assignedProducts.find((p) => p.priceId === priceId);
            return a?.productId ? { productId: a.productId, priceId } : null;
          })
          .filter(Boolean);
        if (items.length) {
          await postUserProducts({
            items,
            type: "assigned",
            userId: client.id,
          });
        }
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
      alert("Failed to save. Please try again.");
    }
  };

  const handleQuantityChange = async (priceId, newValue) => {
    try {
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "updateQuantity",
        value: parseInt(newValue),
      });

      setClientPurchasedProducts((prev) =>
        prev.map((product) =>
          product.priceId === priceId
            ? { ...product, quantityLeft: parseInt(newValue) }
            : product
        )
      );

      setQuantityValues((prev) => ({
        ...prev,
        [priceId]: newValue,
      }));
    } catch (error) {
      console.error("Failed to update quantity:", error);
      alert("Failed to update quantity. Please try again.");
    }
  };

  const productRows = useMemo(() => {
    const purchasedRows = (clientPurchasedProducts || []).map((item) =>
      userProductToRow(item, true)
    );
    const assignedRows = (assignedProducts || []).map((item) =>
      userProductToRow(item, false)
    );
    return [...purchasedRows, ...assignedRows];
  }, [clientPurchasedProducts, assignedProducts]);

  const visibleRows = useMemo(() => {
    if (activeTab === "purchased") {
      const purchased = productRows.filter((row) => row.isPurchased);
      const hasAnyWithQuantity = purchased.some(
        (row) => (row.purchasedProduct?.quantityLeft ?? 0) > 0
      );
      if (hasAnyWithQuantity) {
        return purchased.filter(
          (row) => (row.purchasedProduct?.quantityLeft ?? 0) > 0
        );
      }
      return purchased;
    }
    return productRows.filter((row) => row.isAssigned);
  }, [productRows, activeTab]);

  const canShowCancelButton = (price, purchasedProduct) =>
    (!!price.recurring || !!purchasedProduct?.recurring) &&
    !!purchasedProduct?.subscriptionId;

  const handleCancelSubscription = async (subscriptionId, priceId) => {
    if (!subscriptionId) return;
    setSubscriptionActionLoading(priceId);
    setConfirmState(null);
    try {
      const updated = await cancelSubscription(subscriptionId, stripeAccountId);
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "cancel",
      });
      setClientPurchasedProducts((prev) =>
        prev.map((p) =>
          p.priceId === priceId
            ? {
                ...p,
                cancel_at_period_end: updated?.cancel_at_period_end,
                status: "cancelled",
              }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    }
    setSubscriptionActionLoading(null);
  };

  const handleUncancelSubscription = async (subscriptionId, priceId) => {
    if (!subscriptionId) return;
    setSubscriptionActionLoading(priceId);
    setConfirmState(null);
    try {
      const updated = await uncancelSubscription(
        subscriptionId,
        stripeAccountId
      );
      setClientPurchasedProducts((prev) =>
        prev.map((p) =>
          p.priceId === priceId
            ? { ...p, cancel_at_period_end: updated?.cancel_at_period_end }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to reinstate subscription:", error);
      alert("Failed to reinstate subscription. Please try again.");
    }
    setSubscriptionActionLoading(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Manage Products for {client?.name}</DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab
              value="purchased"
              label={`Purchased (${
                productRows.filter((r) => r.isPurchased).length
              })`}
            />
            <Tab
              value="assigned"
              label={`Assigned (${
                productRows.filter((r) => r.isAssigned).length
              })`}
            />
            <Tab value="transactions" label="Transactions" />
          </Tabs>

          {activeTab === "transactions" ? (
            <ClientSubscriptionTransactions
              profileId={client?.id}
              title="Payment history"
              paperSx={{
                mt: 0,
                p: 0,
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            />
          ) : productsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : visibleRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {activeTab === "purchased"
                ? "No purchased products found for this client."
                : "No assignable products available."}
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    {activeTab === "purchased" ? (
                      <>
                        <TableCell align="center">Billing Date</TableCell>
                        <TableCell align="center">Cancel</TableCell>
                      </>
                    ) : (
                      <TableCell align="center">Assign</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map(
                    ({
                      price,
                      product,
                      purchasedProduct,
                      assignedProduct,
                      isPurchased,
                      isAssigned,
                    }) => (
                      <TableRow key={price.priceId}>
                        <TableCell>{product.name ?? "—"}</TableCell>
                        <TableCell>
                          {price.metadata?.description ?? "—"}
                        </TableCell>
                        <TableCell align="right">
                          {price.unitAmount != null
                            ? `$${Number(price.unitAmount).toFixed(2)}`
                            : "—"}
                        </TableCell>
                        <TableCell align="center">
                          {isPurchased ? (
                            purchasedProduct?.status === "cancelled" ||
                            purchasedProduct?.cancel_at_period_end ? (
                              <Chip
                                label={
                                  purchasedProduct?.cancel_at_period_end
                                    ? "Cancelling"
                                    : "Cancelled"
                                }
                                color="default"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Purchased"
                                color="success"
                                size="small"
                              />
                            )
                          ) : isAssigned ? (
                            <Chip
                              label="Assigned"
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Not Active"
                              color="default"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {price.recurring ? (
                            "n/a"
                          ) : isPurchased ? (
                            <TextField
                              type="number"
                              size="small"
                              value={
                                quantityValues[price.priceId] ??
                                purchasedProduct?.quantityLeft ??
                                0
                              }
                              onChange={(e) =>
                                setQuantityValues((prev) => ({
                                  ...prev,
                                  [price.priceId]: e.target.value,
                                }))
                              }
                              inputProps={{ min: 0 }}
                              sx={{ width: "80px" }}
                            />
                          ) : isAssigned ? (
                            assignedProduct?.quantityLeft ?? 0
                          ) : null}
                        </TableCell>

                        {activeTab === "purchased" ? (
                          <>
                            <TableCell align="center">
                              {(() => {
                                const billingOrStart =
                                  purchasedProduct?.nextBillingDate ||
                                  purchasedProduct?.billingDate ||
                                  purchasedProduct?.startDate;
                                if (price.recurring && billingOrStart) {
                                  return (
                                    <Typography
                                      variant="body2"
                                      whiteSpace="nowrap"
                                    >
                                      {moment(billingOrStart).format(
                                        "MMM D, YYYY"
                                      )}
                                    </Typography>
                                  );
                                }
                                if (billingOrStart) {
                                  return (
                                    <Typography
                                      variant="body2"
                                      whiteSpace="nowrap"
                                    >
                                      {moment(billingOrStart).format(
                                        "MMM D, YYYY"
                                      )}
                                    </Typography>
                                  );
                                }
                                return (
                                  <Typography
                                    color="text.secondary"
                                    variant="body2"
                                  >
                                    {price.recurring ? "—" : "n/a"}
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="center">
                              {canShowCancelButton(price, purchasedProduct) ? (
                                purchasedProduct.cancel_at_period_end ? (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() =>
                                      setConfirmState({
                                        type: "reinstate",
                                        subscriptionId:
                                          purchasedProduct.subscriptionId,
                                        priceId: price.priceId,
                                      })
                                    }
                                    loading={
                                      subscriptionActionLoading ===
                                      price.priceId
                                    }
                                  >
                                    Reinstate
                                  </LoadingButton>
                                ) : (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() =>
                                      setConfirmState({
                                        type: "cancel",
                                        subscriptionId:
                                          purchasedProduct.subscriptionId,
                                        priceId: price.priceId,
                                      })
                                    }
                                    loading={
                                      subscriptionActionLoading ===
                                      price.priceId
                                    }
                                  >
                                    Cancel
                                  </LoadingButton>
                                )
                              ) : (
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  n/a
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell align="center">
                            <Checkbox
                              checked={
                                isAssigned ||
                                selectedProducts.includes(price.priceId)
                              }
                              onChange={() => {
                                setSelectedProducts((prev) =>
                                  prev.includes(price.priceId)
                                    ? prev.filter((id) => id !== price.priceId)
                                    : [...prev, price.priceId]
                                );
                              }}
                              disabled={isPurchased}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!confirmState}
        onClose={() => setConfirmState(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmState?.type === "cancel" && "Cancel Subscription"}
          {confirmState?.type === "reinstate" && "Reinstate Subscription"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmState?.type === "cancel" &&
              "Cancel this subscription at the end of the billing period? The client will retain access until then."}
            {confirmState?.type === "reinstate" &&
              "Reinstate this subscription? It will continue to renew."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (confirmState?.type === "cancel") {
                handleCancelSubscription(
                  confirmState.subscriptionId,
                  confirmState.priceId
                );
              } else if (confirmState?.type === "reinstate") {
                handleUncancelSubscription(
                  confirmState.subscriptionId,
                  confirmState.priceId
                );
              }
            }}
            variant="contained"
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageProductsDialog;
